import { Component, computed, ElementRef, inject, input, Signal, signal } from '@angular/core';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { NO_MEASURE, VbarSeriesComponent } from '../../../shared/dashboard/vbarchart-table/vbar-series/vbar-series.component';
import { createResponsiveBarWidth } from '../../../shared/dashboard/vbarchart-table/vbar-series/responsive-bar-width';
import { VbarStyle } from '../../../shared/dashboard/vbarchart-table/vbar-batch/vbar-batch.component';
import { DisplayService } from '../../../services/display.service';
import { getSchoolaanwezigheidClass, interventielaagDetails } from '../../../services/schoolaanwezigheid';
import { fromPairs, isUndefined, last } from 'lodash-es';
import { getMaandLabelKort, getMaandLabelLang, getMaandLabelLangVoorlopig, isHuidigeMaand, prettyCount } from '@cumlaude/shared-utils';
import { Path } from '../../../services/data-tree';
import { createSelection, SelectionConfig } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { Interventielaag } from '@cumlaude/metadata';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { BarInfo } from '../../../services/stacked-bars';
import { AD_HOC_SELECTION_ID } from '../../../services/leerling-selectie.service';
import { percOfParent } from '../../../services/measures';
import { FilterService } from '../../../services/filter.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SchoolaanwezigheidA, SchoolaanwezigheidRequestContext } from '../aanwezigheid-overzicht.component';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { ExportFilter } from '../../../services/data.service';
import { ExportColumnDef } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { ToggleButtonComponent } from '@cumlaude/shared-components-buttons';
import { AfgeslotenLessenService } from '../../../services/afgesloten-lessen.service';
import { UserService } from '../../../services/user.service';
import { MetricsSeriesComponent } from '../../../shared/dashboard/vbarchart-table/metrics-series/metrics-series.component';

type PiramideBarInfo = BarInfo & { exportData?: { laag: Interventielaag; aantalLeerlingen: number; percentage: number } };

@Component({
	selector: 'app-aanwezigheidspiramide-per-maand',
	templateUrl: './aanwezigheidspiramide-per-maand.component.html',
	styleUrls: ['./aanwezigheidspiramide-per-maand.component.scss'],
	imports: [IconComponent, TooltipDirective, VbarSeriesComponent, ToggleButtonComponent, MetricsSeriesComponent],
})
export class AanwezigheidspiramidePerMaandComponent implements ExportablePanel {
	protected readonly displayService = inject(DisplayService);
	private readonly filterService = inject(FilterService);
	private readonly afgeslotenLessenService = inject(AfgeslotenLessenService);
	private readonly userService = inject(UserService);
	private readonly elementRef = inject(ElementRef);

	readonly requestContext = input.required<SchoolaanwezigheidRequestContext>();

	interventielagen = Object.values(Interventielaag);
	readonly schooljaar: Signal<string | undefined> = toSignal(this.filterService.observeAsInput('aw_nm_schooljaar'));
	readonly toonAfgeslotenLessen = signal(false);

	// Dynamische barWidth zodat de grafiek meeschaalt i.p.v. buiten het paneel te tekenen
	readonly barWidth = createResponsiveBarWidth(this.elementRef, 12);

	readonly maandPartitions = computed(() => {
		const context = this.requestContext();
		const maandPaths = fromPairs((context.dataRoot?.r ?? []).map((path) => [last(path)!.k!, path]));

		// Zorg dat we altijd alle 12 maanden tonen van aug t/m jul, ook als er geen data voor is
		const maanden = Array.from({ length: 12 }, (_, i) => (((i + 7) % 12) + 1).toString());

		return maanden.map((nr_maand) => {
			const voorlopig = this.isVoorlopig(nr_maand);
			const maandLabel = getMaandLabelKort(nr_maand);
			const maandLabelLang = getMaandLabelLangVoorlopig(this.schooljaar(), nr_maand);
			const path = maandPaths[nr_maand];
			if (isUndefined(path))
				return {
					qty: 100,
					stacks: [[{ size: 1, className: 'onbekend' }]],
					measure: NO_MEASURE,
					label: maandLabel,
					nr_maand,
					path: [],
					className: 'onbekend',
				};

			const stacks: PiramideBarInfo[][] = last(path)!.xr!.map((subpath) => {
				const size = last(subpath)!.a.aw_m_nr_leerlingen;
				const perc = this.getLlnPercentage(subpath);
				const laag = last(subpath)!.k! as Interventielaag;
				return [
					{
						size,
						className: getSchoolaanwezigheidClass(this.schooljaar(), nr_maand, laag),
						text: perc,
						selection: this.createMaandSelection(context, subpath),
						tooltip: [{ label: '', value: maandLabelLang, bold: true }, laag, prettyCount(size, 'leerling', 'leerlingen')],
						exportData: {
							laag,
							aantalLeerlingen: size,
							percentage: percOfParent('aw_m_nr_leerlingen', 'aw_m_nr_leerlingen')(subpath),
						},
					},
				];
			});
			return {
				qty: 100,
				stacks,
				measure: NO_MEASURE,
				nr_maand,
				label: maandLabel,
				path,
				className: voorlopig ? 'voorlopig' : undefined,
			};
		});
	});

	private isVoorlopig(nr_maand: string): boolean {
		return isHuidigeMaand(this.schooljaar(), nr_maand);
	}

	protected getLlnPercentage(path: Path<SchoolaanwezigheidA, number[]>) {
		return getFormattedValue(percOfParent('aw_m_nr_leerlingen', 'aw_m_nr_leerlingen')(path), 'percentage');
	}

	protected createMaandSelection(context: SchoolaanwezigheidRequestContext, subpath: Path<SchoolaanwezigheidA, number[]>) {
		if (this.userService.isBestuur()) return undefined;

		const x_leerlingselectie = JSON.stringify([{ id: AD_HOC_SELECTION_ID, naam: '' }]);
		const config: SelectionConfig<SchoolaanwezigheidA> = {
			detailUrl: `/aanwezigheid/overzicht?x_leerlingselectie=${x_leerlingselectie}`,
			mergeQueryParams: true,
			provider: 'schoolaanwezigheid',
			getSize: (path: Path<SchoolaanwezigheidA, number[]>) => last(path)!.a.aw_m_nr_leerlingen,
			extraFilters: [],
		};
		return createSelection(context, subpath, config);
	}

	afgeslotenLessen = this.afgeslotenLessenService.createAfgeslotenLessenSignal(
		computed(() => this.requestContext().filter),
		this.toonAfgeslotenLessen
	);

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		const columns: ExportColumnDef[] = [
			{ name: '', type: 'string' },
			{ name: 'Leerlingen', groupName: 'Goede aanwezigheid', type: 'number' },
			{ name: '%', type: 'percentage', format: '1.0-0' },
			{ name: 'Leerlingen', groupName: 'Beginnend verzuim', type: 'number' },
			{ name: '%', type: 'percentage', format: '1.0-0' },
			{ name: 'Leerlingen', groupName: 'Aanhoudend verzuim', type: 'number' },
			{ name: '%', type: 'percentage', format: '1.0-0' },
		];
		return {
			name: 'Piramide per maand',
			...(exportOptions.showTitle ? { titles: ['Piramide per maand', `Niet cumulatief schooljaar ${this.schooljaar()}`] } : {}),
			...(exportOptions.showFilters ? { filters } : {}),
			data: [
				{
					columns,
					rows: this.maandPartitions().map((maandPartition) => {
						const exportDataStacks = maandPartition.stacks.map((stack) => last(stack)).map((barInfo) => barInfo?.exportData);
						const goedeAanwezigheidStack = exportDataStacks.find((exportData) => exportData?.laag === Interventielaag.GOEDE_AANWEZIGHEID);
						const beginnendVerzuimStack = exportDataStacks.find((exportData) => exportData?.laag === Interventielaag.BEGINNEND_VERZUIM);
						const aanhoudendVerzuimStack = exportDataStacks.find((exportData) => exportData?.laag === Interventielaag.AANHOUDEND_VERZUIM);
						return [
							{ value: getMaandLabelLang(maandPartition.nr_maand) },
							{ value: goedeAanwezigheidStack?.aantalLeerlingen },
							{ value: goedeAanwezigheidStack?.percentage },
							{ value: beginnendVerzuimStack?.aantalLeerlingen },
							{ value: beginnendVerzuimStack?.percentage },
							{ value: aanhoudendVerzuimStack?.aantalLeerlingen },
							{ value: aanhoudendVerzuimStack?.percentage },
						];
					}),
				},
			],
		};
	}

	protected readonly Style = VbarStyle;
	protected readonly interventielaagDetails: Record<Interventielaag, string> = interventielaagDetails;
}
